import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Heading from '../../components/Heading';
import EmptyState from './components/EmptyState';
import { Contact } from 'models/Contact';
import api from 'api';
import useQueryParams from 'hooks/useQueryParams';
import useSearchParams from 'hooks/useSearchParams';
import { Loader } from 'ncoded-component-library';
import ResolvePagination from 'components/ResolvePagination';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import ManageContactDrawer from '../../components/ManageContactDrawer';
import { ContactsTable } from './components/ContactsTable';
import ContactsCards from './components/ContactsCards';
import ContactsFilterControls from './components/ContactsFilterControls';
import useMatchMedia from 'hooks/useMatchMedia';
import confirm from 'modules/confirm';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import ContactsProvider from '../../providers/Contacts';
import ContactsActionMenu from './components/ContactsActionMenu';
import useRefetch from 'hooks/useRefetch';
import HelmetWrapper from 'components/HelmetWrapper';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import { getIsArrayEmpty, getHasActiveFilters } from 'utils';
import useTableFetchState from 'hooks/useTableFetchState';
import useContactsFilterOptions from './components/ContactsFilterControls/useContactsFilterOptions';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import useMultiSort from 'hooks/useMultiSort';
import useResetPage from '../../../../../hooks/useResetPage';
import {
  HEADING_TRANSLATIONS,
  MULTI_SORT_DIRECTION_COPY,
  SORTING_VALUES,
  TABLE_COLUMNS,
  REQUIRED_COLUMNS,
  LOCKED_COLUMN,
} from './components/ContactsTable/components/ManagePeopleTableCells/data';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import useDrawerNavigation from 'hooks/useDrawerNavigation';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import ManageColumns from 'components/ManageColumns';

import './ContactsList.styles.scss';
import './ContactsList.styles.responsive.scss';

type ContactsListProps = {
  className?: string;
};

const ContactsList: React.FC<ContactsListProps> = (props) => {
  const { className } = props;

  const {
    isLoading,
    totalPages,
    totalItems,
    data: contacts,
    setIsLoading,
    setData,
    setPaginatedData,
  } = useTableFetchState<Contact>();

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const { refetchFlag, refetch } = useRefetch();

  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedContact, setSelectedContact] = useState<Contact>();

  const isPhablet = useMatchMedia('isPhablet');

  const [manageContactDrawer, manageContactDrawerRef] =
    useCallbackRef<SideDrawerRef>();

  const { popSuccess, popServerError } = useContext(PopNotificationsContext);

  const classes = classNames('contacts-list', className);

  const {
    params: { page },
  } = useQueryParams();

  const searchParams = useSearchParams();

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const isEmpty = !isLoading && getIsArrayEmpty(contacts);

  const { filterOptions } = useContactsFilterOptions();

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    TABLE_COLUMNS,
    SORTING_VALUES,
    HEADING_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onReorder,
    onColumnSizingChange,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.CONTACTS,
    TABLE_COLUMNS,
  );

  const manageColumns = useMemo(
    () => allColumns.filter((column) => !REQUIRED_COLUMNS.includes(column)),
    [allColumns],
  );

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, searchParams),
    [filterOptions, searchParams],
  );

  const updateContacts = useCallback(
    (contact: Contact) => {
      if (contacts.some((c) => c._id === contact._id)) {
        setData((old) => old.map((c) => (c._id === contact._id ? contact : c)));
      } else {
        refetch();
      }
    },
    [contacts, refetch, setData],
  );

  const handleEditContact = useCallback(
    (contact: Contact) => {
      setSelectedContact(contact);
      manageContactDrawer.setOpen(true);
    },
    [manageContactDrawer],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        await confirm(
          {
            title: t('areYouSureDeleteContact'),
            content: t('deleteWarning'),
            type: 'delete',
          },
          true,
        )
      ) {
        try {
          setIsDeleting(true);
          await api.contacts.deleteContact(id);
          popSuccess(t('contactDeleted'));
          refetch();
        } catch (e) {
          popServerError(e);
        } finally {
          setIsDeleting(false);
        }
      }
    },
    [popServerError, popSuccess, t, refetch],
  );

  useResetPage(totalPages, page);

  const { onPrev, onNext } = useDrawerNavigation({
    rows: contacts,
    selectedId: selectedContact?._id,
    totalPages,
    isLoading,
    onNavigate: handleEditContact,
  });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const { data } = await api.contacts.getContacts({
          $limit: limit,
          ...searchParams,
        });

        setPaginatedData(data);
      } catch (e) {
        return;
      } finally {
        setIsLoading(false);
      }
    })();
  }, [page, refetchFlag, searchParams, limit, setPaginatedData, setIsLoading]);

  return (
    <ContactsProvider contacts={contacts} refetch={refetch}>
      <HelmetWrapper title={t('contacts')} />

      <div className={classes}>
        <Heading title={t('contacts')} onSuccess={updateContacts}>
          {(!isEmpty || hasActiveFilters) && (
            <>
              {!isEmpty && (
                <NumberOfResultsPerPageFilter
                  limit={limit}
                  updateLimit={updateLimit}
                />
              )}
              <ContactsFilterControls
                filterOptions={filterOptions}
                multiSortValues={multiSortValues}
                multiSortDirectionTranslation={multiSortDirectionTranslation}
                multiFilterRef={multiFilterRef}
                multiSortRef={multiSortRef}
              />
            </>
          )}
          <ManageColumns
            allColumns={manageColumns}
            hiddenColumns={hiddenColumns}
            headingTranslations={HEADING_TRANSLATIONS}
            lockedColumn={LOCKED_COLUMN}
            isDefaultColumns={isDefaultColumns}
            onSave={saveColumnSettings}
            onResetToDefault={resetToDefault}
          />
        </Heading>

        <ActiveFilterSortPills
          filterOptions={filterOptions}
          sortOptions={multiSortValues}
          onChipLabelClick={onChipLabelClick}
          onSortChipLabelClick={onSortChipLabelClick}
        />

        {isLoading && isPhablet && <Loader />}

        {isEmpty && !hasActiveFilters ? (
          <div className="white-panel">
            <EmptyState updateContacts={refetch} />
          </div>
        ) : (
          <>
            <ContactsActionMenu />

            {isPhablet ? (
              <>
                <ContactsCards
                  contacts={contacts}
                  handleDelete={handleDelete}
                  handleEdit={handleEditContact}
                />
                <ResolvePagination totalPages={totalPages} />
              </>
            ) : (
              <ContactsTable
                contacts={contacts}
                isLoading={isLoading}
                isDeleting={isDeleting}
                totalPages={totalPages}
                totalItems={totalItems}
                columns={columns}
                columnSizing={columnSizing}
                handleEdit={handleEditContact}
                onReorder={onReorder}
                onColumnSizingChange={onColumnSizingChange}
              />
            )}

            <ManageContactDrawer
              contact={selectedContact}
              ref={manageContactDrawerRef}
              onSuccess={updateContacts}
              onDelete={handleDelete}
              onPrev={onPrev}
              onNext={onNext}
            />
          </>
        )}
      </div>
    </ContactsProvider>
  );
};

export default ContactsList;
